import {
  API_ROUTES,
  savedViewSchema,
  searchHitSchema,
  type QueryFilters,
  type SavedView,
  type SearchHit,
} from '@hvac/contracts';
import { queryOptions } from '@tanstack/react-query';
import { z } from 'zod';
import { apiClient } from './queries.js';

/** The query engine: run a structured filter; keep collections of them. */

export const queryEngineKeys = {
  run: (organizationId: string, filters: QueryFilters) =>
    ['query', organizationId, 'run', filters] as const,
  collections: (organizationId: string) => ['query', organizationId, 'collections'] as const,
} as const;

export function runQueryQuery(organizationId: string, filters: QueryFilters) {
  return queryOptions({
    queryKey: queryEngineKeys.run(organizationId, filters),
    queryFn: ({ signal }): Promise<SearchHit[]> =>
      apiClient.request({
        path: API_ROUTES.query.run(organizationId),
        method: 'POST',
        body: { filters },
        schema: z.array(searchHitSchema),
        signal,
      }),
    staleTime: 10_000,
  });
}

export function collectionsQuery(organizationId: string) {
  return queryOptions({
    queryKey: queryEngineKeys.collections(organizationId),
    queryFn: ({ signal }): Promise<SavedView[]> =>
      apiClient.request({
        path: API_ROUTES.query.collections(organizationId),
        schema: z.array(savedViewSchema),
        signal,
      }),
    staleTime: 30_000,
  });
}

export async function createCollection(
  organizationId: string,
  name: string,
  filters: QueryFilters,
): Promise<SavedView> {
  return apiClient.request({
    path: API_ROUTES.query.collections(organizationId),
    method: 'POST',
    body: { name, filters },
    schema: savedViewSchema,
  });
}

export async function deleteCollection(
  organizationId: string,
  collectionId: string,
): Promise<void> {
  await apiClient.request({
    path: API_ROUTES.query.collection(organizationId, collectionId),
    method: 'DELETE',
    schema: z.object({ deleted: z.boolean() }),
  });
}
